import React, { useState } from "react";
import { Plus, Search, Filter, Trash2 } from "lucide-react";
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
} from "../api/services/user/type";
import { UserTable } from "../components/users/UserTable";
import { UserForm } from "../components/users/UserForm";
import { Pagination } from "../components/common/Pagination";
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from "../hooks/useUser";

import { toast } from "react-hot-toast";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useRoles } from "../hooks/useRoles";

export const Staffs: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const itemsPerPage = 10;

  const { data: rolesData, isLoading: rolesLoading } = useRoles({
    limit: 50,
  });

  const roles = rolesData?.roles || [];
  const roleOptions = [
    { value: "", label: "All Roles" },
    ...roles.map((role) => ({
      value: role.name,
      label:
        role.name.charAt(0) +
        role.name.slice(1).toLowerCase().replace("_", " "),
    })),
  ];

  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch: refreshUsers,
  } = useUsers({
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
    search: searchTerm || undefined,
    role: selectedRole || undefined,
  });

  const createUserMutation = useCreateUser();

  const users = usersData?.users || [];
  const totalPages = usersData ? Math.ceil(usersData.total / itemsPerPage) : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateUser = () => {
    setShowForm(true);
  };

  const handleFormSubmit = (data: CreateUserRequest | UpdateUserRequest) => {
    if (editingUser) {
      updateUserMutation.mutate(
        { userId: editingUser.id, userData: data as UpdateUserRequest },
        {
          onSuccess: () => {
            toast.success("User updated successfully!");
            setShowForm(false);
            setEditingUser(undefined);
            refreshUsers();
          },
          onError: (error: Error) => {
            toast.error(
              `Failed to update user: ${error?.message || "Unknown error"}`,
            );
          },
        },
      );
    } else {
      createUserMutation.mutate(data as CreateUserRequest, {
        onSuccess: () => {
          toast.success("User created successfully!");
          setShowForm(false);
          refreshUsers();
        },
        onError: (error: Error) => {
          toast.error(
            `Failed to create user: ${error?.message || "Unknown error"}`,
          );
        },
      });
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(undefined);
  };

  const handleViewUser = (user: User) => {
    console.log("Viewing user:", user);
    // Navigate to user details page or open modal
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDeleteUser = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete, {
        onSuccess: () => {
          toast.success("User deleted successfully!");
          setUserToDelete(null);
          setDeleteDialogOpen(false);
          refreshUsers();
        },
        onError: (error: Error) => {
          toast.error(
            `Failed to delete user: ${error?.message || "Unknown error"}`,
          );
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Users
            </h3>
            <p className="text-red-300">
              {error?.message || "Failed to load users. Please try again."}
            </p>
            <button
              onClick={() => refreshUsers()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search, Filter and Add Button */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Filter
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <select
                value={selectedRole}
                onChange={handleRoleFilter}
                className="pl-10 pr-8 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none min-w-[150px]"
                disabled={rolesLoading}
              >
                {rolesLoading ? (
                  <option value="">Loading roles...</option>
                ) : (
                  roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <button
              onClick={handleCreateUser}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add User
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="mb-6">
          <UserTable
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onView={handleViewUser}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* User Form Modal */}
        {showForm && (
          <UserForm
            user={editingUser}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isLoading={
              createUserMutation.isPending || updateUserMutation.isPending
            }
          />
        )}
      </div>
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        icon={<Trash2 className="h-8 w-8 text-white" />}
        iconBgColor="bg-red-600"
        confirmColor="bg-red-600 hover:bg-red-700"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
